import React, { useState, useEffect } from "react";
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import * as Icons from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * DynamicDatasourceForm - A dynamic form generator for datasource configuration
 * @param {Object} config - Configuration object containing fieldGroups and datasourceTypes
 * @param {string} selectedDatasource - Currently selected datasource type (e.g., 'mysql', 'postgresql')
 * @param {Function} onSubmit - Callback function when form is submitted
 * @param {Function} onTestConnection - Callback function for test connection button
 * @param {boolean} isTestingConnection - Whether connection test is in progress
 * @param {boolean} testConnectionSuccess - Whether test connection was successful
 * @param {Array} existingDatasources - Existing datasource records used for duplicate name and connection validation
 * @param {Array} existingIntegrations - Existing integration records from the integrations API used for duplicate integration name validation
 * @param {string} currentIntegrationName - The current integration name (for edit mode self-exclusion)
 */
export default function DynamicDatasourceForm({
  config, 
  selectedDatasource, 
  onSubmit, 
  onTestConnection,
  isEditMode,
  isTestingConnection = false,
  testConnectionSuccess = null,
  testConnectionResponse = null,
  testConnectionError = null,
  initialFormData = null,
  onFormDataChange,
  onContinueSchemaSelection,
  existingDatasources = [],
  currentDatasourceId = null,
  ip_address = null,
  existingIntegrations = [],
  currentIntegrationName = null
}) {

  // Password visibility state for each password field
  const [passwordVisibility, setPasswordVisibility] = useState({});

  const togglePasswordVisibility = (fieldName) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  
  // Get all field groups to render (global + datasource-specific)
  const getAllFieldGroups = () => {
    const globalGroups = [];
    const datasourceConfig = config.datasourceTypes[selectedDatasource];
    const datasourceGroups = datasourceConfig?.fieldGroups || [];
    return [...globalGroups, ...datasourceGroups];
  };

  // Initialize form data with default values
  const getInitialFormData = () => {
    const initialData = {};
    const allGroups = getAllFieldGroups();
    allGroups.forEach(group => {
      if (group.fields) {
        group.fields.forEach(field => {
          if (field.default !== undefined) {
            initialData[field.name.trim()] = field.default;
          }
        });
      }
    });
    
    // Merge with initialFormData if provided (for edit mode)
    if (initialFormData) {
      return { ...initialData, ...initialFormData };
    }
    
    return initialData;
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  // State for test connection response (stages)
  const [testConnectionResponseState, setTestConnectionResponseState] = useState(testConnectionResponse);
  const [testConnectionErrorState, setTestConnectionErrorState] = useState(null);
  const [testConnectionSuccessState, setTestConnectionSuccessState] = useState(null);
  const currentTestConnectionResponse = testConnectionResponse || testConnectionResponseState;
  const currentTestConnectionError = testConnectionError || testConnectionErrorState;
  const currentTestConnectionSuccess = testConnectionSuccess === null
    ? testConnectionSuccessState
    : testConnectionSuccess;
  // State to track elapsed time for each stage (in seconds)
  const [stageElapsedTime, setStageElapsedTime] = useState({});
  const duplicateDatasourceNameError = "Datasource with this name already exists.";
  const duplicateDatasourceConfigError = "Duplicate Datasource found";

  const normalizeDatasourceName = (value) => String(value || "").trim().toLowerCase();

  const getDatasourceName = (datasource) => {
    if (typeof datasource === "string") return datasource;
    if (Array.isArray(datasource)) {
      return datasource[1]?.value || "";
    }
    return datasource?.datasource_name || "";
  };

  const getDatasourceId = (datasource) => datasource?.datasource_id || datasource?.id || datasource?.value;
  const isDatasourceNameField = (fieldName) => fieldName === "datasource_name";
  const isIntegrationNameField = (fieldName) => fieldName === "integration_name";

  const getDatasourceConfig = () => config.datasourceTypes[selectedDatasource] || {};
  const getIdentityFields = () => (getDatasourceConfig().identityFields || []).map((identityField) => (
    typeof identityField === "string"
      ? { name: identityField, formFields: [identityField], configFields: [identityField], caseInsensitive: true }
      : { caseInsensitive: true, ...identityField }
  ));
  const normalizeIdentityValue = (value, caseInsensitive) => {
    const normalizedValue = value === null || value === undefined ? "" : String(value).trim();
    return caseInsensitive ? normalizedValue.toLowerCase() : normalizedValue;
  };
  const getFirstValue = (source, fieldNames = []) => {
    for (const fieldName of fieldNames) {
      if (source?.[fieldName] !== null && source?.[fieldName] !== undefined && String(source[fieldName]).trim() !== "") {
        return source[fieldName];
      }
    }
    return "";
  };
  const getDatasourceType = (datasource) => datasource?.datasource_type || datasource?.connector_type || datasource?.integration_type || "";
  const normalizeConnectorType = (value) => String(value || "").trim().toLowerCase();

  const getDuplicateDatasourceConfig = (candidateData) => {
    const identityFields = getIdentityFields();
    if (!identityFields.length) return null;

    const identityValues = identityFields.map((identityField) => ({
      definition: identityField,
      value: normalizeIdentityValue(
        getFirstValue(candidateData, identityField.formFields),
        identityField.caseInsensitive
      )
    }));

    // A partial connection is not enough to identify a duplicate.
    if (identityValues.some(({ value }) => !value)) return null;

    const currentId = currentDatasourceId === null || currentDatasourceId === undefined ? "" : String(currentDatasourceId);
    const connectorType = normalizeConnectorType(getDatasourceConfig().connectorType || selectedDatasource);
    const datasourceRecords = Array.isArray(existingDatasources) ? existingDatasources : [];

    return datasourceRecords.find((datasource) => {
      const existingId = getDatasourceId(datasource);
      if (currentId && existingId !== undefined && existingId !== null && String(existingId) === currentId) {
        return false;
      }

      if (normalizeConnectorType(getDatasourceType(datasource)) !== connectorType) return false;

      const existingConfig = datasource?.datasource_config || datasource?.integration_config || datasource || {};
      return identityValues.every(({ definition, value }) => (
        normalizeIdentityValue(
          getFirstValue(existingConfig, definition.configFields),
          definition.caseInsensitive
        ) === value
      ));
    });
  };

  const isDuplicateDatasourceConfig = (candidateData) => Boolean(getDuplicateDatasourceConfig(candidateData));

  const getDuplicateDatasourceConfigErrors = (candidateData) => {
    const duplicateDatasource = getDuplicateDatasourceConfig(candidateData);
    if (!duplicateDatasource) return {};

    const duplicateDatasourceName = getDatasourceName(duplicateDatasource);
    const errorMessage = duplicateDatasourceName
      ? `${duplicateDatasourceConfigError}: ${duplicateDatasourceName}`
      : duplicateDatasourceConfigError;

    return getIdentityFields().reduce((duplicateErrors, identityField) => {
      const fieldName = identityField.formFields?.[0];
      if (fieldName) duplicateErrors[fieldName] = errorMessage;
      return duplicateErrors;
    }, {});
  };

  const isDuplicateIntegrationName = (value) => {
    const integrationName = String(value || "").trim().toLowerCase();
    if (!integrationName) return false;

    // In edit mode, skip the check if the value matches the original name (self-exclusion)
    if (isEditMode && currentIntegrationName) {
      const originalName = String(currentIntegrationName || "").trim().toLowerCase();
      if (integrationName === originalName) return false;
    }

    const integrationRecords = Array.isArray(existingIntegrations) ? existingIntegrations : [];
    return integrationRecords.some((integration) => {
      const existingName = String(integration.integration_name || "").trim().toLowerCase();
      return existingName === integrationName;
    });
  };

  const isDuplicateDatasourceName = (value) => {
    const datasourceName = normalizeDatasourceName(value);
    if (!datasourceName) return false;

    const currentId = currentDatasourceId === null || currentDatasourceId === undefined ? "" : String(currentDatasourceId);
    const currentName = normalizeDatasourceName(initialFormData?.datasource_name);
    const datasourceRecords = Array.isArray(existingDatasources) ? existingDatasources : [];

    return datasourceRecords.some((datasource) => {
      const existingName = normalizeDatasourceName(getDatasourceName(datasource));
      if (!existingName || existingName !== datasourceName) return false;

      const existingId = getDatasourceId(datasource);
      if (currentId && existingId !== undefined && existingId !== null && String(existingId) === currentId) {
        return false;
      }

      if (isEditMode && !currentId && currentName && datasourceName === currentName) {
        return false;
      }

      return true;
    });
  };

  // Initialize form data with defaults when component mounts or datasource changes
  useEffect(() => {
    const initialData = getInitialFormData();
    setFormData(initialData);
    setErrors({});
    // eslint-disable-next-line
  }, [selectedDatasource, initialFormData]);

  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  // Check if field should be shown based on showWhen condition
  const shouldShow = (field) => {
    if (!field.showWhen) return true;

    const conditions = Array.isArray(field.showWhen)
      ? field.showWhen
      : Object.keys(field.showWhen).map((key) => ({
          field: key,
          value: field.showWhen[key]
        }));

    return conditions.every((condition) => {
      const key = condition.field.trim();
      const actualValue = formData[key];
      const expectedValue = condition.value;

      return Array.isArray(expectedValue)
        ? expectedValue.includes(actualValue)
        : actualValue === expectedValue;
    });
  };

  // Validate a single field
  const validateField = (field, value) => {
    let error = "";
    const fieldName = field.name.trim();

    (field.validations || []).forEach(rule => {
      if (rule.type === "required" && !value) {
        error = rule.message;
      }
      if (rule.type === "regex" && value && !(new RegExp(rule.pattern).test(value))) {
        error = rule.message;
      }
    });

    if (!error && isDatasourceNameField(fieldName) && isDuplicateDatasourceName(value)) {
      error = duplicateDatasourceNameError;
    }

    if (!error && isIntegrationNameField(fieldName) && isDuplicateIntegrationName(value)) {
      error = "Integration with this name already exists.";
    }

    return error;
  };

  // Handle field change
  const handleChange = (field, e) => {
    const value = field.input === "checkbox"
      ? e.target.checked
      : field.input === "file"
        ? (e.target.files && e.target.files[0] ? e.target.files[0] : "")
        : e.target.value;
    const fieldName = field.name.trim();
    const newData = { ...formData, [fieldName]: value };
    setFormData(newData);
    setTestConnectionResponseState(null);
    setTestConnectionErrorState(null);
    setTestConnectionSuccessState(null);

    const error = validateField(field, value);
    const duplicateErrors = getDuplicateDatasourceConfigErrors(newData);
    const clearedIdentityErrors = getIdentityFields().reduce((clearedErrors, identityField) => {
      const identityFieldName = identityField.formFields?.[0];
      if (identityFieldName) clearedErrors[identityFieldName] = "";
      return clearedErrors;
    }, {});
    setErrors({ ...errors, ...clearedIdentityErrors, [fieldName]: error, ...duplicateErrors });
  };

  // Render icon component
  const renderIcon = (iconName) => {
    if (!iconName) return null;
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={18} color="#A4A7AE" /> : null;
  };

  // Render individual field
  const renderField = (field) => {
    if (!shouldShow(field)) return null;
    const fieldName = field.name.trim();
    const err = errors[fieldName];

    // Collect additional attributes (exclude known ones)
    let extraAttrs = {};
    const knownAttrs = [
      'readonly','name', 'label', 'input', 'icon', 'placeholder', 'colClass', 'validations', 'options', 'default', 'showWhen', 'checkboxLabel', 'accept', 'separator', 'defaultValue', 'value', 'type', 'onChange', 'className', 'as', 'disabled'
    ];
    extraAttrs = Object.keys(field)
      .filter(attr => !knownAttrs.includes(attr) && attr !== 'readonly')
      .reduce((acc, attr) => {
        acc[attr] = field[attr];
        return acc;
      }, {});

    // For any field with readonly in config, add both readOnly and readonly in edit mode
    if (isEditMode && (field.readonly === true || field.readonly === 'true' || ['host_url', 'database_name', 'host', 'database'].includes(fieldName))) {
      extraAttrs.readOnly = true;
      extraAttrs.readonly = 'true'; // Ensures readonly="true" in DOM for HTML and React compatibility
    }

  return (
    <Form.Group key={fieldName} className={field.colClass || "col-lg-6"}>
    <Form.Label>
      {field.label} {field.validations?.some(v => v.type === "required") && <span className="text-danger">*</span>}
    </Form.Label>

    {/* Text and Password inputs with eye icon for password */}
    {(field.input === "text" || field.input === "password") && (
      <div className="icon-input-group" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <i>{renderIcon(field.icon)}</i>
      <Form.Control
        type={field.input === "password" ? (passwordVisibility[fieldName] ? "text" : "password") : field.input}
        placeholder={field.placeholder}
        value={formData[fieldName] || ""}
        className={err ? "is-invalid" : ""}
        onChange={(e) => handleChange(field, e)}
        name={fieldName}
        {...extraAttrs}
      />
      {field.input === "password" && (
        <span
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', zIndex: 2 }}
        onClick={() => togglePasswordVisibility(fieldName)}
        role="button"
        aria-label={passwordVisibility[fieldName] ? 'Hide password' : 'Show password'}
        >
        {passwordVisibility[fieldName] ? <Icons.EyeOff size={18} color="#A4A7AE" /> : <Icons.Eye size={18} color="#A4A7AE" />}
        </span>
      )}
      </div>
    )}

        {/* Select dropdown */}
        {field.input === "select" && (
          <div className="icon-input-group">
            <i>{renderIcon(field.icon)}</i>
            <Form.Control
              as="select"
              className={err ? "is-invalid" : ""}
              // Preserve an explicitly selected empty placeholder value. Using `||`
              // here caused cleared selects to fall back to their configured default.
              value={formData[fieldName] ?? field.default ?? ""}
              onChange={(e) => handleChange(field, e)}
              name={fieldName}
              disabled={isEditMode && (field.readonly === true || field.readonly === 'true')}
              {...extraAttrs}
            >
              <option value="">{field.placeholder || "Select an option"}</option>
              {field.options && field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Control>
          </div>
        )}

        {/* Radio buttons */}
        {field.input === "radio" && (
          <div className="custom-control custom-radio d-sm-flex pl-0">
            {field.options.map((opt, index) => (
              <Form.Check 
                key={opt}
                custom
                type="radio"
                id={`${fieldName}-${index}`}
                label={opt}
                name={fieldName}
                value={opt}
                checked={formData[fieldName] === opt}
                className={index < field.options.length - 1 ? "mr-4" : ""}
                onChange={(e) => handleChange(field, e)}
                {...extraAttrs}
              />
            ))}
          </div>
        )}

        {/* Checkbox */}
        {field.input === "checkbox" && (
          <Form.Check 
            custom
            type="checkbox"
            id={fieldName}
            label={field.checkboxLabel || ""}
            checked={!!formData[fieldName]}
            onChange={(e) => handleChange(field, e)}
            {...extraAttrs}
          />
        )}

        {/* File input */}
        {field.input === "file" && (
          <Form.Control
            type="file"
            accept={field.accept || undefined}
            className={err ? "is-invalid" : ""}
            onChange={(e) => handleChange(field, e)}
            name={fieldName}
            {...extraAttrs}
          />
        )}

        {/* Error message */}
        {err && <div className="error-text mt-1">{err}</div>}
      </Form.Group>
    );
  };

  // Get all fields from all groups (for validation)
  const getAllFields = () => {
    const allGroups = getAllFieldGroups();
    const fields = [];
    allGroups.forEach(group => {
      if (group.fields) {
        fields.push(...group.fields);
      }
    });
    return fields;
  };

  // Render a field group
  const renderFieldGroup = (group, groupIndex) => {
    // Check if any field in this group should be shown
    const hasVisibleFields = group.fields.some(field => shouldShow(field));
    if (!hasVisibleFields) return null;

    return (
      <React.Fragment key={group.id || groupIndex}>
        {/* Separator before group if specified */}
        {group.separator && groupIndex > 0 && <hr className="mt-0" />}
        
        <div className="form-row">
          {group.fields.map(field => renderField(field))}
        </div>
      </React.Fragment>
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all visible fields
    let validationErrors = {};
    const allFields = getAllFields();
    
    allFields.forEach(field => {
      if (shouldShow(field)) {
        const fieldName = field.name.trim();
        const value = formData[fieldName];
        const error = validateField(field, value);
        if (error) validationErrors[fieldName] = error;
      }
    });
    Object.assign(validationErrors, getDuplicateDatasourceConfigErrors(formData));
    
    setErrors(validationErrors);

    // If no errors, submit
    if (Object.keys(validationErrors).length === 0) {
      onSubmit(formData);
    }
  };

  // Update local state if prop changes (for controlled usage)
  useEffect(() => {
    setTestConnectionResponseState(testConnectionResponse);
  }, [testConnectionResponse]);

  // Handle stage elapsed time: all stages update simultaneously after 2 seconds
  useEffect(() => {
    if (testConnectionResponseState && Array.isArray(testConnectionResponseState.stages)) {
      const stages = testConnectionResponseState.stages;
      const initialTime = {};
      stages.forEach((_, idx) => {
        initialTime[idx] = 0;
      });
      setStageElapsedTime(initialTime);

      const interval = setInterval(() => {
        setStageElapsedTime(prev => {
          const newTime = { ...prev };
          let allDone = true;
          stages.forEach((_, idx) => {
            if (newTime[idx] < 2) {
              newTime[idx] = (newTime[idx] || 0) + 1;
              allDone = false;
            }
          });
          if (allDone) {
            clearInterval(interval);
          }
          return newTime;
        });
      }, 1000);

      // Cleanup interval on unmount or when stages change
      return () => {
        clearInterval(interval);
      };
    }
  }, [testConnectionResponseState]);

  // Reset stage elapsed time when test connection response is cleared
  useEffect(() => {
    if (!testConnectionResponse) {
      setStageElapsedTime({});
    }
  }, [testConnectionResponse]);

  // Handle test connection
  const handleTestConnection = async () => {


    // Validate required fields before testing
    let validationErrors = {};
    const allFields = getAllFields();
    
    allFields.forEach(field => {
      if (shouldShow(field)) {
        const fieldName = field.name.trim();
        const value = formData[fieldName];
        const error = validateField(field, value);
        if (error) validationErrors[fieldName] = error;
      }
    });
    Object.assign(validationErrors, getDuplicateDatasourceConfigErrors(formData));
    
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      // Support async or sync onTestConnection
      const payload = { ...formData, check_type: "datasource_check" };
      if (isEditMode) {
        payload.datasource_id = currentDatasourceId;
      }
      const result = await onTestConnection(payload);
      if (result && typeof result === 'object') {
        setTestConnectionResponseState(result);
        setTestConnectionSuccessState(result.status_code === 200);
        setTestConnectionErrorState(result.status_code === 200
          ? null
          : result.message || result.status_message || "Connection failed.");
      }
    }
  };

  const allFieldGroups = getAllFieldGroups();
  // console.log("formdata:", formData);
  // console.log("this.state.formData:", this.state.formData);
  const isTestConnectionDisabled = isTestingConnection || isDuplicateDatasourceName(formData.datasource_name) || isDuplicateIntegrationName(formData.integration_name) || isDuplicateDatasourceConfig(formData);

  return (
    <form onSubmit={handleSubmit}>
      {/* Render field groups */}
      {allFieldGroups.map((group, index) => renderFieldGroup(group, index))}

      {/* Test Connection Alert */}
      {onTestConnection && (
        <div className="form-row">
          <div className="col-lg-12">
            <Alert 
              variant="" 
              className={`tw-w-full !tw-rounded-lg tw-border tw-mt-2 !tw-p-0 ${
                currentTestConnectionSuccess === true
                  ? 'tw-border-[#ABEFC6] tw-bg-[#ECFDF3] dark:tw-bg-[#0E2A1C] dark:tw-border-[#1B5E3C]' 
                  : 'tw-border-[#C7D7FE] tw-bg-[#EEF4FF] dark:tw-bg-[#161622] dark:tw-border-[#2B2B40]'
              }`}
            >
              <div className="md:tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-3 lg:tw-py-4">
                <div className="tw-flex tw-items-center">
                  <i className="tw-me-3 tw-ms-0 lg:tw-mx-3">
                    <svg 
                      className={currentTestConnectionSuccess === true ? 'tw-text-[#067647] dark:tw-text-[#34D399]' : 'tw-text-[#3538CD] dark:tw-text-[#53B1FD]'}
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M17 19C16.7348 19 16.4804 18.8946 16.2929 18.7071C16.1054 18.5196 16 18.2652 16 18V16C16 15.4696 16.2107 14.9609 16.5858 14.5858C16.9609 14.2107 17.4696 14 18 14H20C20.5304 14 21.0391 14.2107 21.4142 14.5858C21.7893 14.9609 22 15.4696 22 16V18C22 18.2652 21.8946 18.5196 21.7071 18.7071C21.5196 18.8946 21.2652 19 21 19M17 19H21M17 19V21M21 19V21M19 14V6.5C19 5.57174 18.6313 4.6815 17.9749 4.02513C17.3185 3.36875 16.4283 3 15.5 3C14.5717 3 13.6815 3.36875 13.0251 4.02513C12.3687 4.6815 12 5.57174 12 6.5V17.5C12 18.4283 11.6313 19.3185 10.9749 19.9749C10.3185 20.6313 9.42826 21 8.5 21C7.57174 21 6.6815 20.6313 6.02513 19.9749C5.36875 19.3185 5 18.4283 5 17.5V10M3 5V3M3 5C2.73478 5 2.48043 5.10536 2.29289 5.29289C2.10536 5.48043 2 5.73478 2 6V8C2 8.53043 2.21071 9.03914 2.58579 9.41421C2.96086 9.78929 3.46957 10 4 10H6C6.53043 10 7.03914 9.78929 7.41421 9.41421C7.78929 9.03914 8 8.53043 8 8V6C8 5.73478 7.89464 5.48043 7.70711 5.29289C7.51957 5.10536 7.26522 5 7 5M3 5H7M7 5V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </i>
                  <div>
                    <h4 className={`tw-mb-1 tw-text-sm lg:tw-text-base tw-font-semibold ${
                      currentTestConnectionSuccess === true ? 'tw-text-[#067647] dark:tw-text-[#34D399]' : 'tw-text-[#3538CD] dark:tw-text-[#53B1FD]'
                    }`}>
                      Test your connection
                    </h4>
                    <p className={`m-0 tw-text-sm lg:tw-text-base ${
                      currentTestConnectionSuccess === true ? 'tw-text-[#535862] dark:tw-text-[#B2B5BE]' : 'tw-text-[#444CE7] dark:tw-text-[#B2B5BE]'
                    }`}>
                      Verify your credentials are correct
                    </p>
                  </div>
                </div>
                {/* Hide this button once the testing is started */}
                <button 
                  type="button"
                  className={`btn tw-mt-3 md:tw-mt-0 tw-ms-9 md:tw-ms-0 ${
                    currentTestConnectionSuccess === true
                      ? 'tw-text-white hover:tw-text-white tw-bg-[#079455] dark:tw-bg-[#10B981]' 
                      : 'btn-primary'
                  }`}
                  onClick={handleTestConnection}
                  disabled={isTestConnectionDisabled}
                >
                  {/* {console.log("DynamicDatasourceForm.js")} */}
                  {isTestingConnection ? (
                    <div className="tw-flex tw-items-center tw-justify-center">
                      <div className="tw-w-[14px] tw-h-[14px] tw-border tw-border-solid tw-rounded-full tw-border-white tw-border-t-transparent tw-animate-spin"></div>
                      <span className="tw-ml-2">Testing...</span>
                    </div>
                  ) : (
                    <span>Test Connection</span>
                  )}
                </button>
              </div>
              
              {/* Render validation stages or error message after test connection attempt */}
              {(currentTestConnectionResponse !== null || currentTestConnectionError) && (Array.isArray(currentTestConnectionResponse?.stages) || currentTestConnectionError) && (
                <div className="tw-px-3 tw-py-3 lg:tw-px-6">
                  <div className='tw-flex tw-flex-col tw-gap-[6px]'>
                    {currentTestConnectionError && (
                      <div className="tw-py-3 tw-px-4 tw-flex tw-items-center tw-gap-3 tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-white/10 tw-bg-white dark:tw-bg-white/[0.03] tw-rounded-xl">
                        <i>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#D92D20" strokeWidth="2" fill="none"></circle>
                            <path d="M9 9L15 15M15 9L9 15" stroke="#D92D20" strokeWidth="2" strokeLinecap="round"></path>
                          </svg>
                        </i>
                        <div>
                          <h6 className="tw-text-sm tw-font-medium tw-m-0 tw-text-[#D92D20] dark:tw-text-[#F97066]">
                            {currentTestConnectionError}
                          </h6>
                        </div>
                      </div>
                    )}
                    {Array.isArray(currentTestConnectionResponse?.stages) && currentTestConnectionResponse.stages.map((stage, idx) => (
                      <div key={stage.name || idx} className={`tw-py-3 tw-px-4 tw-flex tw-items-center tw-gap-3 tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-white/10 tw-bg-white dark:tw-bg-white/[0.03] tw-rounded-xl ${currentTestConnectionError ? 'd-none' : ''}`}>
                        <i>
                          {/* Show spinner for 2 seconds, then show actual status */}
                          {(stageElapsedTime[idx] || 0) < 2 ? (
                            <div className="tw-w-[24px] tw-h-[24px] tw-border-2 tw-border-solid tw-rounded-full tw-border-[#D1E9FF] tw-border-t-[#0021A5] dark:tw-border-t-[#53B1FD] tw-animate-spin"></div>
                          ) : stage.status === true ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#079455" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : stage.status === false || stage.status === null ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" stroke="#D92D20" strokeWidth="2" fill="none" />
                              <path d="M9 9L15 15M15 9L9 15" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          ) : (
                            <div className="tw-w-[24px] tw-h-[24px] tw-border-2 tw-border-solid tw-rounded-full tw-border-[#D1E9FF] tw-border-t-[#0021A5] dark:tw-border-t-[#53B1FD] tw-animate-spin"></div>
                          )}
                        </i>
                        <div>
                          <h6 className={`tw-text-sm tw-font-medium tw-m-0 ${
                            (stageElapsedTime[idx] || 0) < 2 ? 'tw-text-[#0021A5] dark:tw-text-[#53B1FD]' :
                            stage.status === true ? 'tw-text-[#079455] dark:tw-text-[#34D399]' :
                            stage.status === false || stage.status === null ? 'tw-text-[#D92D20] dark:tw-text-[#F97066]' :
                            'tw-text-[#0021A5] dark:tw-text-[#53B1FD]'
                          }`}>{stage.name}</h6>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Alert>
            <div className="tw-flex tw-items-center">  
              {currentTestConnectionResponse && currentTestConnectionResponse?.status_code !== 200 && (
                  <div className="alert alert-info d-inline-flex align-items-center m-0 tw-mt-4">
                      <h5 className="mr-2 mr-sm-3 mb-0 text-info"><FontAwesomeIcon icon={faInfoCircle} /></h5>
                      <span>Make sure Qualdo's IP : <strong>{ip_address}</strong> is added to your authorised network(s).</span>
                  </div>
              )}
              </div>
            {/* {console.log("Test Connection Response State:", selectedDatasource)} */}
            {
            (selectedDatasource !== 'neo4j' && selectedDatasource !== 'amazon_neptune' && selectedDatasource !== 'azure_cosmos_gremlin' && selectedDatasource !== 'spanner_graph') &&
            currentTestConnectionSuccess === true && testConnectionResponseState && Array.isArray(testConnectionResponseState.stages) && (
              <div className='tw-flex tw-items-center tw-justify-end tw-my-4'>
                <button
                    name="specifiedSchemasNext"
                    onClick={onContinueSchemaSelection}
                    className='btn btn-outline-primary tw-mt-3 sm:tw-mt-0 tw-inline-flex tw-items-center'>
                    {selectedDatasource === "azure_blob" || selectedDatasource === "s3" ? "Continue Bucket & Mode Selection" : "Continue Schema & Mode Selection"}
                    <i className='tw-ms-1'>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            >
                            <mask
                                id="mask0_26824_209148"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="20"
                                height="20"
                            >
                                <rect width="20" height="20" fill="#D9D9D9" />
                            </mask>

                            <g mask="url(#mask0_26824_209148)">
                                <path
                                d="M11.6673 15L10.5007 13.7917L13.459 10.8333H3.33398V9.16667H13.459L10.5007 6.20833L11.6673 5L16.6673 10L11.6673 15Z"
                                fill="currentColor"
                                />
                            </g>
                            </svg>
                    </i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );
}
